// Copyright 2025, University of Maryland, All Rights Reserved
import type { FC } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui'
import type { NodeAdminViewProps } from '../../../../core/types'
import { useTextExtractionAdminConfig } from './useTextExtractionAdminConfig'
import { ExtractionMethodSection } from './ExtractionMethodSection'
import { DocxOptionsSection } from './DocxOptionsSection'
import {
  DatasourceConfigSection,
  type DatasourceType,
  type OriginalDocumentDatasourceSettings,
} from '@/subsystems/datasources'

export const TextExtractionAdminView: FC<NodeAdminViewProps> = ({
  config,
  onChange,
  orgId,
  workspaceId,
}) => {
  const {
    detectFormatting,
    setDetectFormatting,
    forceExtraction,
    setForceExtraction,
    extractTrackedChanges,
    setExtractTrackedChanges,
    acceptTrackedChanges,
    setAcceptTrackedChanges,
    extractComments,
    setExtractComments,
  } = useTextExtractionAdminConfig({ config, onChange })

  // Extract datasource settings from config
  const datasourceType = (config?.datasourceType as DatasourceType) ?? 'original_document'
  const datasourceConfig = config?.datasourceConfig as OriginalDocumentDatasourceSettings | undefined

  const handleDatasourceTypeChange = (type: DatasourceType) => {
    onChange?.('datasourceType', type)
  }

  const handleDatasourceConfigChange = (key: string, value: unknown) => {
    // Update the nested datasourceConfig object
    onChange?.('datasourceConfig', {
      ...(datasourceConfig ?? {}),
      [key]: value,
    })
  }

  return (
    <div className="space-y-4">
      <DatasourceConfigSection
        datasourceType={datasourceType}
        onDatasourceTypeChange={handleDatasourceTypeChange}
        datasourceConfig={datasourceConfig}
        onConfigChange={handleDatasourceConfigChange}
        allowedTypes={['original_document']}
        orgId={orgId}
        workspaceId={workspaceId}
        title="Document Source"
        description="Configure where documents are read from for text extraction"
      />

      <Card>
        <CardHeader>
          <CardTitle>Text Extraction Configuration</CardTitle>
          <CardDescription>
            Configure text extraction settings for document processing
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <ExtractionMethodSection
            detectFormatting={detectFormatting}
            onDetectFormattingChange={setDetectFormatting}
            forceExtraction={forceExtraction}
            onForceExtractionChange={setForceExtraction}
          />

          <DocxOptionsSection
            extractTrackedChanges={extractTrackedChanges}
            onExtractTrackedChangesChange={setExtractTrackedChanges}
            acceptTrackedChanges={acceptTrackedChanges}
            onAcceptTrackedChangesChange={setAcceptTrackedChanges}
            extractComments={extractComments}
            onExtractCommentsChange={setExtractComments}
          />

        </CardContent>
      </Card>
    </div>
  )
}
